import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { getVAAccessToken } from "../../utils/parseId";
import { toast } from "react-toastify";
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { getSessionToken } from "../../lib/sessionToken";

export default function PatientPage({ setAuth }) {
  const isStandaloneUser = getIsStandaloneUser();
  const { patient_id } = useParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [created, setCreated] = useState(false);
  const hasRun = useRef(false);
  const [accessToken, setAccessToken] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAccessToken = async () => {
      const token = await getVAAccessToken();
      setAccessToken(token);

      // Reset hasRun when we get a new token so the patient fetch can run
      if (token) {
        hasRun.current = false;
      }
    };

    fetchAccessToken();
  }, []);

  const formatDate = (date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };
  // todays date in "Day, Month Date, Year" format IE "Monday, January 1, 2026"
  const today = formatDate(new Date());

  const fhirUrl = localStorage.getItem('va_fhir_server');

  useEffect(() => {
    if (isStandaloneUser) {
      toast.info("Creating VA patients is disabled for standalone users.");
      navigate("/dashboard");
      return;
    }

    if (hasRun.current) return; // Prevent multiple executions

    // Don't proceed if we don't have required data
    if (!accessToken || !patient_id) {
      // console.log('Waiting for access token and patient_id...', {
      //   accessToken: accessToken ? 'present' : 'missing',
      //   patient_id: patient_id || 'missing'
      // });
      return;
    }

    hasRun.current = true;

    const fetchOrCreatePatient = async () => {
      // console.log('🚀 Starting patient fetch with token:', accessToken.substring(0, 10) + '...');
      setLoading(true);

      try {
        // 1. Try to fetch patient from DB
        const dbRes = await fetch(`${process.env.REACT_APP_API_URL}/va/patient/today?patient_id=${patient_id}&today=${today}`,
          {
            headers: {
              "Content-Type": "application/json",
              token: getSessionToken()
            }
          }
        );
        const dbData = await dbRes.json();

        if (dbData) {
          // console.log('Patient already exists in database');
          setCreated(false);
          navigate('/dashboard');
        } else {
          // 2. If not found, create patient using VA FHIR API
          // console.log('Fetching patient data from VA FHIR API...');
          toast.info("Fetching patient data from VA...");

          const fhirRes = await fetch(
            `${fhirUrl}/Patient/${patient_id}`,
            {
              headers: {
                accept: "application/fhir+json",
                authorization: `Bearer ${accessToken}`,
              },
            }
          );

          // Validate FHIR response
          if (!fhirRes.ok) {
            console.error(`FHIR API error: ${fhirRes.status} ${fhirRes.statusText}`);
            throw new Error(`Failed to fetch patient data from VA FHIR API: ${fhirRes.status}`);
          }

          const fhirData = await fhirRes.json();

          // Validate FHIR data structure
          if (!fhirData || fhirData.resourceType !== 'Patient') {
            console.error('Invalid FHIR response:', fhirData);
            throw new Error('Invalid patient data received from VA FHIR API');
          }

          // console.log('✅ Valid FHIR data received, creating patient in database...');
          // console.log('FHIR Patient Data:', fhirData);

          // 3. Only create patient if FHIR data is valid
          const createRes = await fetch(`${process.env.REACT_APP_API_URL}/va/patient/create`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token: getSessionToken()
            },
            body: JSON.stringify({
              appointmentId: patient_id,
              dateOfAppointment: today,
              patientData: fhirData,
            }),
          });

          if (!createRes.ok) {
            const errorData = await createRes.text();
            console.error('Failed to create patient in database:', errorData);
            throw new Error(`Failed to create patient record: ${createRes.status}`);
          }

          const createdPatient = await createRes.json();
          // console.log('✅ Patient created successfully:', createdPatient);
          setCreated(true);

          navigate(`/record/${createdPatient.patient_id}`);
        }
      } catch (err) {
        console.error("Error fetching or creating patient:", err);
        setPatient(null);

        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchOrCreatePatient();
  }, [patient_id, accessToken, today, navigate, fhirUrl, isStandaloneUser]);

  // Spinner component (same motif as Dashboard)
  const Spinner = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white bg-opacity-80">
      <div aria-hidden="true" className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#783BFF] mb-6"></div>
      <div className="text-lg text-accent1 font-semibold">Loading patient data...</div>
    </div>
  );

  // Full-page modal style container
  return (
    <>
      <Helmet><title>Patient Setup – Knowtex</title></Helmet>
      <main
        id="main-content"
        tabIndex={-1}
        className="fixed inset-0 z-50 flex items-center justify-center bg-gray-100 focus:outline-none"
      >
        <h1 className="sr-only">Patient setup</h1>
        <div role="status" className="w-full max-w-2xl bg-white rounded-xl shadow-xl p-8 flex flex-col items-center">
        {loading ? (
          <Spinner />
        ) : created ? (
          // Show success message when patient is created
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg aria-hidden="true" className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-4 text-green-600">
              Patient Record Created Successfully!
            </h2>
            <div className="mb-2 text-gray-700">
              <strong>Patient ID:</strong> {patient_id}
            </div>
            <div className="mb-4 text-gray-700">
              <strong>Date:</strong> {today}
            </div>
            <div className="text-sm text-gray-500">
              Redirecting to dashboard...
            </div>
          </div>
        ) : (
          // This should rarely show since we redirect on errors too
          <div className="text-center text-red-600 font-semibold">
            Unable to load patient information.
          </div>
        )}
        </div>
      </main>
    </>
  );
}
